// PublicChannelPage — публичная shareable-страница канала (GROWTH-OS-v9 петля 3).
// Без auth. Это и есть "артефакт" — пользователь пересылает эту ссылку в чат/пост,
// получатель видит бренд teleclaw и CTA "Попробовать бесплатно" (петля 1: visit→signup).
//
// ⚠️ Известное ограничение: SPA без SSR — Telegram/соцсети link-preview (og:tags) не
// генерируются динамически на сервере для этой страницы (только статичные из index.html).
// Полноценный unfurl-preview потребует SSR/prerender — вне рамок этой итерации.
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Users, Zap } from 'lucide-react';
import { api } from '../api/client';

interface PublicChannelData {
  title: string;
  handle: string;
  subscribers: number;
  er7d: number | null;
  growthRate30d: number | null;
  badgeUrl: string;
}

export function PublicChannelPage(): JSX.Element {
  const { handle } = useParams<{ handle: string }>();
  const { t } = useTranslation();

  const { data, isLoading, isError } = useQuery<PublicChannelData>({
    queryKey: ['public-channel', handle],
    queryFn: async () => (await api.get<PublicChannelData>(`/public/channel/${handle}`)).data,
    enabled: Boolean(handle),
    retry: false,
  });

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-brand-muted">{t('common.loading')}</div>;
  }
  if (isError || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-brand-muted">
        <p>{t('publicChannel.notFound')}</p>
        <Link to="/" className="text-brand-primary hover:underline">teleclaw.ncide.com</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-primary/10 to-slate-50 p-6 flex flex-col items-center">
      <div className="max-w-xl w-full">
        <div className="text-center mb-6">
          <div className="text-2xl font-bold text-brand-primary">teleclaw</div>
          <p className="text-sm text-brand-muted">{t('publicChannel.tagline')}</p>
        </div>

        <div className="card overflow-hidden">
          <img src={data.badgeUrl} alt={data.title} className="w-full h-auto" />
          <div className="p-6">
            <h1 className="text-xl font-bold text-brand-ink mb-4">{data.title}{data.handle ? ` @${data.handle}` : ''}</h1>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <Users size={20} className="mx-auto text-brand-primary mb-1" />
                <div className="text-lg font-semibold">{data.subscribers.toLocaleString()}</div>
                <div className="text-xs text-brand-muted">{t('publicChannel.subscribers')}</div>
              </div>
              <div className="text-center">
                <Zap size={20} className="mx-auto text-brand-primary mb-1" />
                <div className="text-lg font-semibold">{data.er7d != null ? `${data.er7d.toFixed(1)}%` : '—'}</div>
                <div className="text-xs text-brand-muted">{t('publicChannel.er')}</div>
              </div>
              <div className="text-center">
                <TrendingUp size={20} className="mx-auto text-brand-primary mb-1" />
                <div className="text-lg font-semibold">{data.growthRate30d != null ? `${data.growthRate30d >= 0 ? '+' : ''}${data.growthRate30d.toFixed(1)}%` : '—'}</div>
                <div className="text-xs text-brand-muted">{t('publicChannel.growth')}</div>
              </div>
            </div>
            <Link to="/login" className="btn-primary w-full text-center block">
              {t('publicChannel.cta')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
